export const INPUT_CLS = 'w-full bg-th-input border border-th-border-2 rounded-lg px-3 py-2 text-sm text-th-text placeholder-th-text-4 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500';
export const LABEL_CLS = 'block text-xs font-medium text-th-text-3 mb-1';

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'datetime-local';
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  step?: string;
}

export interface FormModalOptions {
  title: string;
  fields: FormField[][];
  submitLabel?: string;
  onSubmit: (data: Record<string, string>) => void;
  onClose: () => void;
}

export function createFormModal(opts: FormModalOptions): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black/60';
  overlay.setAttribute('data-modal-overlay', '');

  const fieldRows = opts.fields.map(row => {
    const cols = row.length;
    const fields = row.map(f => {
      let input: string;
      if (f.type === 'select') {
        const optionsHtml = (f.options || []).map(o => `<option value="${o.value}">${o.label}</option>`).join('');
        input = `<select name="${f.name}" ${f.required ? 'required' : ''} class="${INPUT_CLS}"><option value="">Select...</option>${optionsHtml}</select>`;
      } else {
        const attrs = [
          `name="${f.name}"`,
          `type="${f.type}"`,
          f.required ? 'required' : '',
          f.placeholder ? `placeholder="${f.placeholder}"` : '',
          f.step ? `step="${f.step}"` : '',
        ].filter(Boolean).join(' ');
        input = `<input ${attrs} class="${INPUT_CLS}" />`;
      }
      return `<div><label class="${LABEL_CLS}">${f.label}${f.required ? ' *' : ''}</label>${input}</div>`;
    }).join('');
    return `<div class="grid grid-cols-${cols} gap-4">${fields}</div>`;
  }).join('');

  overlay.innerHTML = `
    <div class="bg-th-deep border border-th-border-2 rounded-2xl w-full max-w-lg mx-4 shadow-2xl">
      <div class="flex items-center justify-between px-6 py-4 border-b border-th-border">
        <h2 class="text-lg font-bold">${opts.title}</h2>
        <button data-modal-close class="text-th-text-3 hover:text-th-text transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <form data-modal-form class="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
        ${fieldRows}
        <div data-form-error class="text-red-400 text-sm hidden"></div>
        <div class="flex justify-end gap-3 pt-2">
          <button type="button" data-modal-cancel class="px-4 py-2 text-sm font-medium text-th-text-3 hover:text-th-text transition-colors">Cancel</button>
          <button type="submit" class="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors">${opts.submitLabel || 'Create'}</button>
        </div>
      </form>
    </div>
  `;

  const close = () => { overlay.remove(); opts.onClose(); };
  overlay.querySelector('[data-modal-close]')!.addEventListener('click', close);
  overlay.querySelector('[data-modal-cancel]')!.addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  const form = overlay.querySelector('[data-modal-form]') as HTMLFormElement;
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    const data: Record<string, string> = {};
    fd.forEach((v, k) => { data[k] = v as string; });
    opts.onSubmit(data);
  });

  return overlay;
}

export function showModalError(modal: HTMLElement, message: string) {
  const errEl = modal.querySelector('[data-form-error]') as HTMLElement;
  if (errEl) {
    errEl.textContent = message;
    errEl.classList.remove('hidden');
  }
}
